import { motion, useReducedMotion } from 'framer-motion';

interface AcceptanceHeroProps {
  fullName: string;
  referenceNumber: string;
  updatedAt: string | null;
  prefersReducedMotion?: boolean;
}

const CONFETTI_PIECES = [
  { id: 1, startX: '12%', deltaX: -18, deltaY: -42, rotate: -35, color: '#FDE68A', delay: 0.05 },
  { id: 2, startX: '24%', deltaX: 14, deltaY: -54, rotate: 40, color: '#A7F3D0', delay: 0.1 },
  { id: 3, startX: '38%', deltaX: -10, deltaY: -48, rotate: -25, color: '#DDD6FE', delay: 0.08 },
  { id: 4, startX: '50%', deltaX: 6, deltaY: -60, rotate: 30, color: '#FBCFE8', delay: 0.02 },
  { id: 5, startX: '64%', deltaX: -14, deltaY: -50, rotate: -45, color: '#FDE68A', delay: 0.12 },
  { id: 6, startX: '76%', deltaX: 16, deltaY: -46, rotate: 25, color: '#BAE6FD', delay: 0.06 },
  { id: 7, startX: '88%', deltaX: -12, deltaY: -40, rotate: -30, color: '#A7F3D0', delay: 0.14 },
];

function formatArabicDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export default function AcceptanceHero({
  fullName,
  referenceNumber,
  updatedAt,
  prefersReducedMotion: prefersReducedMotionProp,
}: AcceptanceHeroProps) {
  const hookReducedMotion = useReducedMotion();
  const prefersReducedMotion = prefersReducedMotionProp ?? Boolean(hookReducedMotion);
  const firstName = fullName.trim().split(/\s+/)[0] || fullName;
  const formattedUpdated = formatArabicDate(updatedAt);

  return (
    <section
      aria-label="تهنئة القبول المبدئي"
      className="relative overflow-hidden rounded-3xl border border-[#5B3FD0]/20 bg-gradient-to-br from-[#5B3FD0] via-[#6C4CE0] to-[#4729B0] p-6 text-center text-white shadow-md sm:p-8"
    >
      {!prefersReducedMotion && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          {CONFETTI_PIECES.map((piece) => (
            <motion.span
              key={piece.id}
              initial={{ opacity: 0.95, y: 24, x: 0, scale: 0.6, rotate: 0 }}
              animate={{
                opacity: [0.95, 0.9, 0],
                y: piece.deltaY,
                x: piece.deltaX,
                scale: [0.6, 1, 0.85],
                rotate: piece.rotate,
              }}
              transition={{
                duration: 1.5,
                delay: piece.delay,
                ease: 'easeOut',
              }}
              style={{
                insetInlineStart: piece.startX,
                top: '55%',
                backgroundColor: piece.color,
              }}
              className="absolute h-2.5 w-2.5 rounded-full"
            />
          ))}
        </div>
      )}

      <p className="text-xl tracking-widest sm:text-2xl" aria-hidden="true">
        🎉 ✨ 🎓 ✨ 🎉
      </p>

      <h1 className="mt-3 text-2xl font-extrabold leading-snug sm:text-3xl">
        ألف ألف مبروك يا {firstName}! ❤️
      </h1>

      <p className="mx-auto mt-3 max-w-lg text-sm font-medium leading-7 text-white/95 sm:text-base">
        اجتزت المرحلة الأولى من التصفية لمنحة EduNasser للدراسة في أوروبا 2027/2028
      </p>

      <div className="mt-5 inline-flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-white/25 bg-white/15 px-4 py-2 text-xs sm:text-sm backdrop-blur-xs">
        <span className="font-semibold text-white/90">رقم طلبك:</span>
        <span
          dir="ltr"
          className="rounded-lg bg-white px-2.5 py-0.5 font-mono text-xs font-extrabold tracking-wider text-[#5B3FD0] sm:text-sm"
        >
          {referenceNumber}
        </span>
      </div>

      {formattedUpdated && (
        <p className="mt-3 text-xs font-medium text-white/80">
          آخر تحديث: {formattedUpdated}
        </p>
      )}
    </section>
  );
}
